/* eslint @typescript-eslint/no-var-requires: "off" */
import { SuspendedReason } from '@ecm/portfolio';
import { Link, TrackingData } from '@ecm/router';

import { MegapushModel } from '../../../../components/dashboard/megapush/megapush.model';
import { ConvertUtils } from '../../../../utils/convert.utils';
import { ImageUtils } from '../../../../utils/image.utils';
import { DashboardReco } from '../../../api/dashboard/item/dashboardReco';
import { ItemSection } from '../../../api/dashboard/item/itemSection/itemSection';
import { UnitEnum } from '../../../api/dashboard/item/itemSection/unit.enum';
import { UsageCodeEnum } from '../../../api/dashboard/item/itemSection/usageCodeEnum';
import { PushV7 } from '../../../api/dashboardV7/pushV7';
import { SuspendedViewModel } from '../../../model/common/suspended.view-model';
import { ButtonViewModel } from '../../../model/home/button.view-model';
import { DashboardV7AlertsViewModel } from '../../../model/home/dashboard/dashboardV7-alerts.view-model';
import { DashboardV7HelpsViewModel } from '../../../model/home/dashboard/dashboardV7-helps.view-model';
import { DashboardV7WarnsViewModel } from '../../../model/home/dashboard/DashboardV7WarnsViewModel';
import { EquipmentLinks } from '../../../model/home/dashboard/equipments-links';
import { PushSubItemViewModel } from '../../../model/home/pushSubItem.view-model';
import { RelationalV7ViewModel } from '../../../model/home/relationalV7.view-model';
import { SubItemViewModel } from '../../../model/home/subItem.view-model';
import { SubItemTypeEnum } from '../../../model/home/subItemTypeEnum';
import { WhoUsesItViewModel } from '../../../model/home/whoUsesIt.view-model';
import { KenobiCodes } from '../../../model/tracking/kenobiCodes';
import { KeyTmsCodes } from '../../../model/tracking/keyTmsCodes';
import { TmsCodes } from '../../../model/tracking/tmsCodes.enum';
import { TrackedName } from '../../../model/tracking/trackedName.enum';
import { TrackedPage } from '../../../model/tracking/trackedPage.enum';

export class DashboardV7MapperUtils {
    public static airboxImage = 'assets/dashboard/images/airbox.png';
    public static flyboxImage = 'assets/dashboard/images/flybox.png';
    public static homeliveImage = 'assets/dashboard/images/homelive.png';
    public static defaultLandlineImage = 'assets/dashboard/images/ligne-fixe-defaut.png';
    public static defaultHomeImage = 'assets/dashboard/images/maison-defaut.png';
    public static defaultMobileImage = 'assets/dashboard/images/mobile-defaut.png';
    public static defaultMultisimImage = 'assets/dashboard/images/multisim-defaut.png';
    public static defaultTabletImage = 'assets/dashboard/images/tablette-defaut.png';
    public static defaultTvImageOrange = 'assets/dashboard/images/tv-divertissement-defaut-orange.png';
    public static defaultTvImageSosh = 'assets/dashboard/images/tv-divertissement-defaut-sosh.png';
    public static defaultProtectedHouse = 'assets/dashboard/images/maison-protegee.png';

    private static readonly FORMAT_NDE_DESKTOP = '280x268';
    private static readonly FORMAT_NDE_MOBILE = '360x310';

    /**
     * Extract and return commercial or relational objet form dashboard
     * @param {PushV7} dashboardReco
     * @returns {RelationalV7ViewModel}
     */
    public static getRelationalInfo(dashboardReco: PushV7): RelationalV7ViewModel {
        const relationalViewModel: RelationalV7ViewModel = Object.assign(new RelationalV7ViewModel(), {
            label: dashboardReco.label,
            code: dashboardReco.code,
            cid: dashboardReco.cid,
            content: dashboardReco.content,
            urlLabel: dashboardReco.urlLabel,
            link: dashboardReco.link,
            title: dashboardReco.title,
            imageUrl: dashboardReco.imageUrl,
            advTracker: dashboardReco.advTracker,
            trackingData: Object.assign(new TrackingData(), {
                tmsTracking: {
                    trackedPage: TrackedPage.dashboard,
                    trackingArea: 'care_hp_top_info',
                    trackingName: dashboardReco.code,
                },
                cappingTracking:
                    dashboardReco.advTracker && dashboardReco.advTracker.length > 0
                        ? {
                              advTracker: dashboardReco.advTracker,
                              code: dashboardReco.code,
                              cid: dashboardReco.cid,
                          }
                        : null,
            }),
        });
        if (Object.prototype.hasOwnProperty.call(dashboardReco, 'imgUrl')) {
            relationalViewModel.imageUrl = ImageUtils.transformUrl(dashboardReco.imageUrl, '32');
        }
        return relationalViewModel;
    }

    public static getAlerts(alerts: PushV7[]): DashboardV7AlertsViewModel[] {
        return alerts.filter((alert: PushV7) => alert.type == 'alert');
    }

    public static getWhoUsesItInfo(whoUsesIt: PushV7): WhoUsesItViewModel {
        return {
            type: whoUsesIt.type,
            cid: whoUsesIt.cid,
            title: whoUsesIt.title,
            content: whoUsesIt.content,
        };
    }

    public static getWarnings(warnings: PushV7[]): DashboardV7WarnsViewModel[] {
        return warnings.filter((warning: PushV7) => warning.type == 'warning');
    }

    /**
     * Extract and return commercial or relational objet form dashboard
     * @param {DashboardReco} dashboardReco
     * @param {boolean} testPrimaryInfo
     * @returns {PushSubItemViewModel}
     */
    public static getCommercialInfo(dashboardReco: DashboardReco, testPrimaryInfo?: boolean): PushSubItemViewModel {
        return Object.assign(new PushSubItemViewModel(), {
            advTracker: dashboardReco.advTracker,
            cid: dashboardReco.cid,
            code: dashboardReco.secondaryCode
                ? `${dashboardReco.code},${dashboardReco.secondaryCode}`
                : dashboardReco.code,
            detail: [dashboardReco.content],
            functionality: KenobiCodes.ACCMS.functionality,
            label: dashboardReco.label,
            link: dashboardReco.link,
            pictoImage: Object.prototype.hasOwnProperty.call(dashboardReco, 'imgUrl')
                ? ImageUtils.transformUrl(dashboardReco.imgUrl, '32')
                : null,
            textPrimary: testPrimaryInfo,
            type: SubItemTypeEnum.commercial,
        });
    }

    /**
     * return mainCredit value. if unit is SMS or MMS, mainCredit is value
     * @param {number} value
     * @param {string} unit
     * @returns {string}
     */
    public static convertMainCredit(value: number, unit: UnitEnum): string {
        let mainCredit: string;
        switch (unit) {
            case UnitEnum.VOLUM: {
                mainCredit = ConvertUtils.convertKiloToMegaOrGiga(value);
                break;
            }
            case UnitEnum.CURRENCY: {
                mainCredit = ConvertUtils.convertCentsToEuros(value);
                break;
            }
            case UnitEnum.TIME: {
                mainCredit = ConvertUtils.convertSecondToTime(value);
                break;
            }
            default:
                mainCredit = value.toString();
        }
        return mainCredit;
    }

    /**
     * Generate equipment path redirection depending on usage code and usage type
     * @param {string} usageCode
     * @param {string} usageType
     * @param {string} idSuiviConso
     * @returns {Link} Equipment path
     */
    public static getEquipmentPath(usageCode: UsageCodeEnum, usageType?: string, idSuiviConso?: string): Link {
        let link: Link = null;
        const equipmentsUrls: Record<string, unknown> = EquipmentLinks[usageCode];
        if (equipmentsUrls) {
            if (usageType) {
                if (Object.prototype.hasOwnProperty.call(equipmentsUrls, usageType)) {
                    link = Object.assign(new Link(), equipmentsUrls[usageType]);
                } else if (
                    Object.prototype.hasOwnProperty.call(equipmentsUrls, 'idSuiviConso') &&
                    idSuiviConso !== null
                ) {
                    link = Object.assign(new Link(), equipmentsUrls['idSuiviConso']);
                } else if (Object.prototype.hasOwnProperty.call(equipmentsUrls, 'default')) {
                    link = Object.assign(new Link(), equipmentsUrls['default']);
                }
            } else {
                if (Object.prototype.hasOwnProperty.call(equipmentsUrls, 'default')) {
                    link = Object.assign(new Link(), equipmentsUrls['default']);
                }
            }
        }
        return link;
    }

    /**
     * Generate conso contract path redirection
     * @param {string} cid
     * @param {string} consoId Conso equipment id
     * @param {any} dashboardTranslations
     * @param {string} equipmentId
     * @returns {Link&{inApp: (void|string|any)}}
     */
    public static getConsoPath(cid: string, consoId: string, dashboardTranslations: any, equipmentId?: string): Link {
        let path: any;
        let link: Link;
        if (equipmentId) {
            path = dashboardTranslations['conso']['routes']['basicFixe']
                .replace('{{cid}}', cid)
                .replace('{{equipmentId}}', equipmentId ? equipmentId : 'equipement-principal');
            link = Object.assign(new Link(), {
                inApp: path,
            });
        } else {
            path = dashboardTranslations['conso']['routes']['basic']
                .replace('{{cid}}', cid)
                .replace('{{idSuiviConso}}', consoId ? consoId : 'equipement-principal');
            link = Object.assign(new Link(), {
                inApp: path,
            });
        }

        return link;
    }

    /**
     * Generate conso contract path redirection
     * @param {string} cid
     * @param {string} equipmentId Conso equipment id
     * @param {any} dashboardTranslations
     * @returns {Link&{inApp: (void|string|any)}}
     */
    public static getConsoPathFixe(cid: string, equipmentId: string, dashboardTranslations: any): Link {
        return Object.assign(new Link(), {
            inApp: dashboardTranslations['conso']['routes']['basicFixe']
                .replace('{{cid}}', cid)
                .replace('{{equipmentId}}', equipmentId ? equipmentId : 'equipement-principal'),
        });
    }

    /**
     * Extract button information from item
     * @param {any} item
     * @param {TmsCodes} tmsCode
     * @param {TrackedName} trackedName
     * @returns {ButtonViewModel | null}
     */
    public static extractButton(item: any, tmsCode: TmsCodes, trackedName?: TrackedName): ButtonViewModel | null {
        let button: ButtonViewModel = null;
        if (Object.prototype.hasOwnProperty.call(item, 'button')) {
            const labelButton: string = Object.prototype.hasOwnProperty.call(item, 'cid') ? item.code : item?.id;
            button = new ButtonViewModel(labelButton);
            button.link = item.link;
            button.trackingArea = tmsCode.toString();
            button.trackingName = trackedName ? trackedName : labelButton;
            const queryParams = {};
            const params = new Map<string, string>();
            if (item.cid) {
                queryParams['carrousel'] = btoa(item.cid);
                params.set('carrousel', btoa(item.cid));
            }
            button.navigationExtras = {};
            button.navigationExtras.queryParams = queryParams;
            button.params = params;
        }
        return button;
    }

    /**
     * extract warn title
     * @param {ItemSection} itemSection
     * @returns {string | null}
     */
    public static extractWarnTitle(itemSection: ItemSection): string {
        return Object.prototype.hasOwnProperty.call(itemSection, 'warn') ? itemSection.warn.title : null;
    }

    /**
     * Extract suspended info
     * @param {ItemSection} itemSection
     * @param {TmsCodes} tmsCode
     * @return {SuspendedViewModel} suspendedContract
     */
    public static extractSuspendedInfo(itemSection: ItemSection, tmsCode: TmsCodes): SuspendedViewModel {
        if (Object.prototype.hasOwnProperty.call(itemSection, 'suspended')) {
            const suspendedContract: SuspendedViewModel = new SuspendedViewModel();
            let trackedName: TrackedName;
            suspendedContract.suspendedReason = itemSection.suspended.suspendedReason;
            suspendedContract.title = itemSection.suspended.title;
            suspendedContract.text = itemSection.suspended.text;
            switch (itemSection.suspended.suspendedReason) {
                case SuspendedReason.LOSS:
                case SuspendedReason.THEFT:
                    trackedName = TrackedName.THEFT_LOSS_BUTTON;
                    break;
                case SuspendedReason.NONPAYMENT:
                    trackedName = TrackedName.NON_PAYMENT_BUTTON;
                    suspendedContract.unpaid = true;
                    break;
                case SuspendedReason.PRESAR:
                    trackedName = TrackedName.NON_PAYMENT_PRESAR_BUTTON;
                    suspendedContract.unpaid = true;
                    break;
                default:
                    break;
            }
            suspendedContract.button = DashboardV7MapperUtils.extractButton(
                itemSection.suspended,
                tmsCode,
                trackedName,
            );
            return suspendedContract;
        }
        return null;
    }

    /**
     * extract tracking data with capping
     * @param {string} advTrackerInfo
     * @param {string} codeInfo
     * @param {string} secondaryCode
     * @return TrackingData
     */
    public static getTrackingCapping(advTrackerInfo: string, codeInfo: string, secondaryCode?: string): TrackingData {
        return Object.assign(new TrackingData(), {
            cappingTracking: {
                advTracker: advTrackerInfo,
                code: secondaryCode ? `${codeInfo},${secondaryCode}` : codeInfo,
            },
            featureName: 'dashboard',
            functionality: KenobiCodes.ACCMS.functionality,
        });
    }

    /**
     * extract megapush datas model
     * @param {Megapush} megapush
     * @return MegapushModel
     */
    public static getMegapushPcm(megapush: PushV7): MegapushModel {
        if (megapush?.imageUrl) {
            return new MegapushModel({
                cid: megapush?.cid,
                energy: megapush?.energy,
                imgUrl: megapush?.imageUrl,
                imgWebMobileUrl: megapush?.imageWebMobileUrl,
                img161: ImageUtils.transformUrlForNde(megapush?.imageWebMobileUrl, this.FORMAT_NDE_DESKTOP),
                img162: ImageUtils.transformUrlForNde(megapush?.imageUrl, this.FORMAT_NDE_MOBILE),
                legal: megapush?.legal,
                tag: megapush?.tag,
                text: typeof megapush?.content === 'string' ? megapush.content : megapush.content[0],
                text2: typeof megapush?.content === 'string' ? null : megapush.content[1],
                textColor: megapush?.textColor,
                title: megapush?.title,
                trackingData: Object.assign(new TrackingData(), {
                    tmsTracking: {
                        trackedPage: TrackedPage.dashboard,
                        trackingArea: 'care_hp_top_megapush',
                        trackingName: megapush?.code,
                    },
                    cappingTracking:
                        megapush?.advTracker && megapush?.advTracker.length > 0
                            ? {
                                  advTracker: megapush.advTracker,
                                  code: megapush?.code,
                                  cid: megapush?.cid,
                              }
                            : null,
                }),
                urlWeb: megapush?.link,
                urlLabel: megapush?.urlLabel,
            });
        } else {
            return null;
        }
    }

    /**
     * extract zid for tracking
     * @param {string} key
     * @param {PushSubItemViewModel} pushSubItem
     * @return PushSubItemViewModel
     */
    public static extractZid(key: string, pushSubItem: PushSubItemViewModel) {
        pushSubItem.zid = KeyTmsCodes[key].zid;
        pushSubItem.zidMob = KeyTmsCodes[key].zidMob;
        pushSubItem.zidSosh = KeyTmsCodes[key].zidSosh;
        pushSubItem.zidSoshMob = KeyTmsCodes[key].zidSoshMob;
        return pushSubItem;
    }

    public static getRecoTv(itemReco: DashboardReco, trackName: string, pico: any): SubItemViewModel {
        return Object.assign(new SubItemViewModel(), {
            type: SubItemTypeEnum.picto,
            cid: itemReco.cid,
            label: itemReco.label,
            detail: [itemReco.content],
            detailLong: [itemReco.content],
            trackingArea: TmsCodes.care_hp_maison,
            trackingName: trackName,
            picto: pico[itemReco.imageId],
            link: itemReco.link,
        });
    }
    public static getInfos(infos: PushV7[]): DashboardV7HelpsViewModel[] {
        return infos.filter((info: PushV7) => info.type == 'info');
    }
}
